import type {CertificateFormat, CertificateLines} from './layouts'
import {renderCertificate} from './paint'

const LETTER_PDF_WIDTH = 792
const LETTER_PDF_HEIGHT = 612

export type CertificateExportOptions = {
  lines: CertificateLines
  templateKey?: string
  assetBaseUrl?: string
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  type = 'image/png',
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('The certificate image could not be created.')),
      type,
      quality
    )
  })
}

export async function renderCertificatePng(
  options: CertificateExportOptions & {format: CertificateFormat}
): Promise<Blob> {
  return canvasToBlob(await renderCertificate(options))
}

export async function renderCertificatePdf(
  options: CertificateExportOptions & {title?: string}
): Promise<Blob> {
  const [canvas, {jsPDF}] = await Promise.all([
    renderCertificate({...options, format: 'letter', scale: 3}),
    import('jspdf'),
  ])
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: [LETTER_PDF_WIDTH, LETTER_PDF_HEIGHT],
  })
  pdf.setProperties({
    title: options.title ?? 'Certificate of Completion',
    subject: options.lines.courseName ?? '',
    keywords: options.lines.credential?.replace('Credential No. ', '') ?? '',
    author: 'Ad Astra Skills Academy',
    creator: 'Ad Astra Skills Academy',
  })
  pdf.addImage(canvas, 'PNG', 0, 0, LETTER_PDF_WIDTH, LETTER_PDF_HEIGHT, undefined, 'FAST')
  return pdf.output('blob')
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
